import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useTheme } from '../contexts/ThemeContext';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { Mail, MessageCircle, Clock, AlertCircle, Ticket, CheckCircle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { btn } from '../utils/buttonStyles';
import { getWebOrigin } from '../utils/helpers';

const SUPPORT_EMAIL = 'support@kynaixsports.com';

function EmailLink({ className, children }) {
  const href = `mailto:${SUPPORT_EMAIL}`;
  if (Capacitor.isNativePlatform()) {
    return (
      <button type="button" onClick={() => window.open(href, '_system')} className={className}>
        {children}
      </button>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}

const CATEGORIES = [
  { value: 'auth',        label: 'Account / Login' },
  { value: 'billing',     label: 'Billing' },
  { value: 'bug',         label: 'Bug Report' },
  { value: 'content',     label: 'Content Issue' },
  { value: 'tournament',  label: 'Tournament' },
  { value: 'general',     label: 'General' },
];

const PRIORITIES = [
  { value: 'low',    label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high',   label: 'High' },
];

export default function ContactPage() {
  const { theme } = useTheme();
  const { setBreadcrumbItems } = useBreadcrumb();
  const { user } = useAuth();
  const { createSupportTicket: featureCreateSupportTicket, listSupportTicket: featureListSupportTicket } = useFeatureFlags();

  const [ticketOpen, setTicketOpen] = useState(false);
  const [form, setForm] = useState({
    summary:     '',
    description: '',
    category:    'general',
    priority:    'medium',
    userName:    '',
    userEmail:   '',
    userPhone:   '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [error, setError] = useState(null);

  const [myTickets, setMyTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [expandedTicket, setExpandedTicket] = useState(null);

  useEffect(() => {
    setBreadcrumbItems([{ label: 'Contact Us', path: '/contact' }]);
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems]);

  // Pre-fill from logged-in user
  useEffect(() => {
    if (user) {
      setForm(f => ({
        ...f,
        userName:  f.userName  || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        userEmail: f.userEmail || user.email || '',
        userPhone: f.userPhone || user.phoneNumber || '',
      }));
    }
  }, [user]);

  const fetchMyTickets = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setTicketsLoading(true);
    try {
      const res = await fetch(`${getWebOrigin()}${import.meta.env.REACT_APP_IMAGE_HANDLER_CONTEXT_PATH}/support-tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setMyTickets(await res.json());
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchMyTickets();
  }, [user]);

  const openHelpChat = () => {
    window.dispatchEvent(new CustomEvent('open-help-chat'));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getWebOrigin()}${import.meta.env.REACT_APP_IMAGE_HANDLER_CONTEXT_PATH}/support-tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        throw new Error('__system_down__');
      }

      const data = await res.json();
      setTicketId(data.ticketId);
      setSubmitted(true);
      fetchMyTickets();

      // Fire confirmation email — non-blocking, don't fail the UX if it errors
      if (form.userEmail) {
        fetch('/exportsapi/notifications/send', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            notification_id: 'SUPPORT_TICKET_CREATED',
            to_email: form.userEmail,
            user_id: user?.id || null,
            data: {
              ticket_id:   data.ticketId.replace(/^USERID-\d+-/, ''),
              user_name:   form.userName  || user?.firstName || 'there',
              user_id:     user?.id       || '',
              summary:     form.summary,
              description: form.description,
              category:    form.category,
              priority:    form.priority,
            },
          }),
        }).catch(() => {});
      }
    } catch {
      setError('__system_down__');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = `w-full border-2 border-gray-300 dark:border-gray-500 rounded-xl px-3 py-2.5 text-sm ${theme.text} ${theme.card} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`;
  const labelClass = `block text-sm font-medium ${theme.text} mb-1`;

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <div className="max-w-7xl mx-auto px-4 py-10">

        {/* Header */}
        <div className="mb-10">
          <h1 className={`text-3xl font-bold ${theme.text} mb-2`}>Contact Us</h1>
          <p className={`${theme.textSecondary} text-lg`}>
            We're here to help. Reach out to the Kynaix Sports team anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">

          {/* Email Support */}
          <div className={`${theme.card} border ${theme.border} rounded-2xl p-6`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <Mail size={22} className="text-blue-600" />
              </div>
              <div>
                <h2 className={`font-semibold text-lg ${theme.text}`}>Email Support</h2>
                <p className={`text-sm ${theme.textSecondary}`}>We typically respond within 24–48 hours</p>
              </div>
            </div>
            <p className={`${theme.textSecondary} text-sm mb-5`}>
              For general inquiries, account issues, billing questions, or anything else — send us an email and our team will get back to you.
            </p>
            <EmailLink className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-xl transition-colors text-sm">
              <Mail size={16} />
              support@kynaixsports.com
            </EmailLink>
          </div>

          {/* Kyna */}
          <div className={`${theme.card} border ${theme.border} rounded-2xl p-6`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                <MessageCircle size={22} className="text-purple-600" />
              </div>
              <div>
                <h2 className={`font-semibold text-lg ${theme.text}`}>Kyna</h2>
                <p className={`text-sm ${theme.textSecondary}`}>Report issues directly to our team</p>
              </div>
            </div>
            <p className={`${theme.textSecondary} text-sm mb-5`}>
              Chat with Kyna to report bugs, submit feedback, or flag any issues. Your report is sent directly to the Kynaix Sports team.
            </p>
            <button
              onClick={openHelpChat}
              className={`inline-flex items-center gap-2 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
            >
              <MessageCircle size={16} />
              Chat with Kyna
            </button>
          </div>
        </div>

        {/* Support Ticket */}
        {featureCreateSupportTicket && (
        <div className={`${theme.card} border ${theme.border} rounded-2xl mb-8 overflow-hidden`}>
          <button
            onClick={() => setTicketOpen(o => !o)}
            className={`w-full flex items-center justify-between p-6 text-left hover:bg-black/5 transition-colors`}
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <Ticket size={22} className="text-green-600" />
              </div>
              <div>
                <h2 className={`font-semibold text-lg ${theme.text}`}>Submit a Support Ticket</h2>
                <p className={`text-sm ${theme.textSecondary}`}>Track your issue with a reference number</p>
              </div>
            </div>
            {ticketOpen
              ? <ChevronUp size={20} className={theme.textSecondary} />
              : <ChevronDown size={20} className={theme.textSecondary} />
            }
          </button>

          {ticketOpen && (
            <div className="px-6 pb-6">
              {submitted ? (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <CheckCircle size={44} className="text-green-500" />
                  <p className={`font-semibold text-lg ${theme.text}`}>Ticket submitted!</p>
                  {ticketId && (
                    <div className={`flex items-center gap-2 ${theme.card} border ${theme.border} rounded-xl px-4 py-2`}>
                      <span className={`text-xs font-medium ${theme.textSecondary}`}>Ticket ID</span>
                      <span className={`text-sm font-mono font-bold ${theme.text}`}>{ticketId.replace(/^USERID-\d+-/, '')}</span>
                    </div>
                  )}
                  <p className={`text-sm ${theme.textSecondary}`}>Save this ID for reference. Our team will review it and follow up via email.</p>
                  <button
                    onClick={() => { setSubmitted(false); setTicketId(null); setForm(f => ({ ...f, summary: '', description: '' })); }}
                    className="mt-2 text-sm text-blue-600 underline"
                  >
                    Submit another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Summary */}
                  <div>
                    <label className={labelClass}>Summary <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="summary"
                      value={form.summary}
                      onChange={handleChange}
                      required
                      maxLength={200}
                      placeholder="Brief description of the issue"
                      className={inputClass}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className={labelClass}>Description <span className="text-red-500">*</span></label>
                    <textarea
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      required
                      rows={4}
                      maxLength={5000}
                      placeholder="Provide as much detail as possible"
                      className={`${inputClass} resize-none`}
                    />
                  </div>

                  {/* Category + Priority */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Category</label>
                      <select name="category" value={form.category} onChange={handleChange} className={inputClass}>
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Priority</label>
                      <select name="priority" value={form.priority} onChange={handleChange} className={inputClass}>
                        {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Contact info */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>Your Name</label>
                      <input type="text" name="userName" value={form.userName} onChange={handleChange} placeholder="Full name" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Email</label>
                      <input type="email" name="userEmail" value={form.userEmail} onChange={handleChange} placeholder="you@example.com" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Phone</label>
                      <input type="tel" name="userPhone" value={form.userPhone} onChange={handleChange} placeholder="+1 555 000 0000" className={inputClass} />
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-start gap-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-xl px-4 py-3">
                      <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700 dark:text-red-300">
                        Our support system is temporarily unavailable. Please email us directly at{' '}
                        <EmailLink className="underline font-medium">
                          {SUPPORT_EMAIL}
                        </EmailLink>{' '}
                        and we'll get back to you within 24–48 hours.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className={`inline-flex items-center gap-2 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
                  >
                    <Ticket size={16} />
                    {submitting ? 'Submitting…' : 'Submit Ticket'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
        )}

        {/* My Tickets */}
        {featureListSupportTicket && user && (
          <div className={`${theme.card} border ${theme.border} rounded-2xl p-6 mb-8`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Ticket size={18} className="text-green-600" />
                <h2 className={`font-semibold ${theme.text}`}>My Tickets</h2>
              </div>
              <button onClick={fetchMyTickets} disabled={ticketsLoading} className="p-1.5 rounded-lg hover:bg-black/5 transition-colors">
                <RefreshCw size={15} className={`${theme.textSecondary} ${ticketsLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {ticketsLoading && !myTickets.length ? (
              <p className={`text-sm ${theme.textSecondary}`}>Loading…</p>
            ) : myTickets.length === 0 ? (
              <p className={`text-sm ${theme.textSecondary}`}>No tickets yet.</p>
            ) : (
              <div className={`divide-y ${theme.border}`}>
                {myTickets.map(t => (
                  <div key={t.ticketId}>
                    {/* Ticket row */}
                    <button
                      onClick={() => setExpandedTicket(expandedTicket === t.ticketId ? null : t.ticketId)}
                      className="w-full flex items-center justify-between py-3 gap-4 text-left hover:bg-black/5 transition-colors rounded-lg px-1"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-xs font-mono font-bold ${theme.text}`}>{t.ticketId.replace(/^USERID-\d+-/, '')}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            t.status === 'open' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                          }`}>{t.status}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            t.priority === 'high' ? 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300' :
                            t.priority === 'medium' ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-300' :
                            'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                          }`}>{t.priority}</span>
                          {t.replies?.length > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                              {t.replies.length} {t.replies.length === 1 ? 'reply' : 'replies'}
                            </span>
                          )}
                        </div>
                        <p className={`text-sm ${theme.text} truncate`}>{t.summary}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-xs ${theme.textSecondary}`}>{new Date(t.createdAt).toLocaleDateString()}</span>
                        {expandedTicket === t.ticketId
                          ? <ChevronUp size={14} className={theme.textSecondary} />
                          : <ChevronDown size={14} className={theme.textSecondary} />
                        }
                      </div>
                    </button>

                    {/* Expanded replies */}
                    {expandedTicket === t.ticketId && (
                      <div className="pb-3 px-1">
                        {t.replies?.length > 0 ? (
                          <div className="space-y-2 mt-1">
                            {t.replies.map(r => (
                              <div key={r.commentId} className={`${theme.card} border ${theme.border} rounded-xl p-3`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className={`text-xs font-medium text-blue-600`}>Kynaix Sports Team</span>
                                  <span className={`text-xs ${theme.textSecondary}`}>{new Date(r.createdAt).toLocaleString()}</span>
                                </div>
                                <p className={`text-sm ${theme.text} whitespace-pre-wrap`}>{r.text}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className={`text-xs ${theme.textSecondary} mt-1 pl-1`}>No replies yet.</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* No phone support notice */}
        <div className={`${theme.card} border ${theme.border} rounded-2xl p-5 flex gap-4 items-start mb-8`}>
          <div className="flex-shrink-0 mt-0.5">
            <AlertCircle size={20} className="text-yellow-500" />
          </div>
          <div>
            <p className={`font-medium ${theme.text} mb-1`}>No phone support at this time</p>
            <p className={`text-sm ${theme.textSecondary}`}>
              Kynaix Sports does not currently offer live phone support. For the fastest response, chat with Kyna to report an issue or email us at{' '}
              <EmailLink className="text-blue-600 underline">support@kynaixsports.com</EmailLink>.
            </p>
          </div>
        </div>

        {/* Response times */}
        <div className={`${theme.card} border ${theme.border} rounded-2xl p-6`}>
          <div className="flex items-center gap-2 mb-4">
            <Clock size={18} className={theme.textSecondary} />
            <h2 className={`font-semibold ${theme.text}`}>Support Hours &amp; Response Times</h2>
          </div>
          <div className={`divide-y ${theme.border}`}>
            {[
              { channel: 'Support Ticket', time: 'Reviewed within 1–2 business days' },
              { channel: 'Kyna (issue reports)', time: 'Reviewed within 1–2 business days' },
              { channel: 'Email — support@kynaixsports.com', time: 'Response within 24–48 hours' },
              { channel: 'Phone', time: 'Not available' },
            ].map(({ channel, time }) => (
              <div key={channel} className="flex justify-between py-3 text-sm">
                <span className={theme.textSecondary}>{channel}</span>
                <span className={`font-medium ${theme.text}`}>{time}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
